import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reservationService } from '../../services/reservationService';
import { vehicleService } from '../../services/vehicleService';
import { fleetService } from '../../services/fleetService';
import { Reservation } from '../../types/reservation';
import { Vehicle } from '../../types/vehicle';
import { Fleet } from '../../types';
// @ts-ignore
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, differenceInDays, addMonths, subMonths } from 'date-fns';

interface ReservationStatsProps {
  organizationId: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ReservationStats: React.FC<ReservationStatsProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [vehicles, setVehicles] = useState<Record<string, Vehicle>>({});
  const [fleets, setFleets] = useState<Record<string, Fleet>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchData();
  }, [organizationId, timeRange, currentDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Déterminer la plage de dates en fonction du timeRange
      let startDate, endDate;
      if (timeRange === 'month') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      } else if (timeRange === 'quarter') {
        const currentMonth = currentDate.getMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        startDate = startOfMonth(new Date(currentDate.getFullYear(), quarterStartMonth, 1));
        endDate = endOfMonth(new Date(currentDate.getFullYear(), quarterStartMonth + 2, 1));
      } else { // year
        startDate = new Date(currentDate.getFullYear(), 0, 1);
        endDate = new Date(currentDate.getFullYear(), 11, 31);
      }

      // Récupérer les réservations pour la période
      const reservationsData = await reservationService.getReservations(
        organizationId,
        {
          startDate,
          endDate
        }
      );
      setReservations(reservationsData);

      // Récupérer les informations sur les véhicules et les flottes
      const vehicleIds = [...new Set(reservationsData.map((r: Reservation) => r.vehicleId))];
      const fleetIds = [...new Set(reservationsData.map((r: Reservation) => r.fleetId))];

      const vehiclesData: Record<string, Vehicle> = {};
      for (const id of vehicleIds) {
        const vehicle = await vehicleService.getVehicle(id);
        if (vehicle) {
          vehiclesData[id] = vehicle;
        }
      }
      setVehicles(vehiclesData);

      const fleetsData: Record<string, Fleet> = {};
      for (const id of fleetIds) {
        const fleet = await fleetService.getFleet(id);
        if (fleet) {
          fleetsData[id] = fleet;
        }
      }
      setFleets(fleetsData);

    } catch (err) {
      console.error('Error fetching reservation stats:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value);
  };

  const navigatePrevious = () => {
    if (timeRange === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (timeRange === 'quarter') {
      setCurrentDate(subMonths(currentDate, 3));
    } else { // year
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), currentDate.getDate()));
    }
  };

  const navigateNext = () => {
    if (timeRange === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (timeRange === 'quarter') {
      setCurrentDate(addMonths(currentDate, 3));
    } else { // year
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate()));
    }
  };

  // Calcul des statistiques
  const calculateStats = () => {
    if (reservations.length === 0) {
      return {
        totalReservations: 0,
        confirmedReservations: 0,
        cancelledReservations: 0,
        pendingReservations: 0,
        averageDuration: 0,
        vehicleUsage: [],
        fleetUsage: [],
        dailyReservations: []
      };
    }

    // Statistiques de base
    const confirmedReservations = reservations.filter((r: Reservation) => r.status === 'confirmed').length;
    const cancelledReservations = reservations.filter((r: Reservation) => r.status === 'cancelled').length;
    const pendingReservations = reservations.filter((r: Reservation) => r.status === 'pending').length;

    // Durée moyenne des réservations (en jours)
    const durations = reservations
      .filter((r: Reservation) => r.status !== 'cancelled')
      .map((r: Reservation) => {
        const start = r.startDate instanceof Date ? r.startDate : r.startDate.toDate();
        const end = r.endDate instanceof Date ? r.endDate : r.endDate.toDate();
        return differenceInDays(end, start) + 1; // +1 pour inclure le jour de début
      });
    const averageDuration = durations.length > 0 
      ? durations.reduce((sum, duration) => sum + duration, 0) / durations.length 
      : 0;

    // Utilisation par véhicule
    const vehicleUsage = Object.entries(vehicles).map(([id, vehicle]) => {
      const vehicleReservations = reservations.filter((r: Reservation) => r.vehicleId === id && r.status !== 'cancelled');
      return {
        name: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`,
        value: vehicleReservations.length,
        id
      };
    }).sort((a, b) => b.value - a.value);

    // Utilisation par flotte
    const fleetUsage = Object.entries(fleets).map(([id, fleet]) => {
      const fleetReservations = reservations.filter((r: Reservation) => r.fleetId === id && r.status !== 'cancelled');
      return {
        name: fleet.name,
        value: fleetReservations.length,
        id
      };
    }).sort((a, b) => b.value - a.value);

    // Réservations par jour
    let startDate, endDate;
    if (timeRange === 'month') {
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
    } else if (timeRange === 'quarter') {
      const currentMonth = currentDate.getMonth();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      startDate = startOfMonth(new Date(currentDate.getFullYear(), quarterStartMonth, 1));
      endDate = endOfMonth(new Date(currentDate.getFullYear(), quarterStartMonth + 2, 1));
    } else { // year
      startDate = new Date(currentDate.getFullYear(), 0, 1);
      endDate = new Date(currentDate.getFullYear(), 11, 31);
    }

    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const dailyReservations = days.map(day => {
      const dayReservations = reservations.filter((r: Reservation) => {
        const start = r.startDate instanceof Date ? r.startDate : r.startDate.toDate();
        return isSameDay(start, day);
      });
      return {
        date: format(day, 'dd/MM'),
        reservations: dayReservations.length
      };
    });

    return {
      totalReservations: reservations.length,
      confirmedReservations,
      cancelledReservations,
      pendingReservations,
      averageDuration,
      vehicleUsage,
      fleetUsage,
      dailyReservations
    };
  };

  const stats = calculateStats();

  const getTimeRangeLabel = () => {
    if (timeRange === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (timeRange === 'quarter') {
      const currentMonth = currentDate.getMonth();
      const quarterNumber = Math.floor(currentMonth / 3) + 1;
      return `Q${quarterNumber} ${currentDate.getFullYear()}`;
    } else { // year
      return currentDate.getFullYear().toString();
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {t('reservation:statistics.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="time-range-label">{t('reservation:statistics.timeRange')}</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              label={t('reservation:statistics.timeRange')}
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="month">{t('reservation:statistics.month')}</MenuItem>
              <MenuItem value="quarter">{t('reservation:statistics.quarter')}</MenuItem>
              <MenuItem value="year">{t('reservation:statistics.year')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="button" onClick={navigatePrevious} sx={{ cursor: 'pointer' }}>
              &lt;
            </Typography>
            <Typography variant="subtitle1">
              {getTimeRangeLabel()}
            </Typography>
            <Typography variant="button" onClick={navigateNext} sx={{ cursor: 'pointer' }}>
              &gt;
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Cartes de statistiques */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reservation:statistics.totalReservations')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalReservations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reservation:statistics.confirmedReservations')}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.confirmedReservations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reservation:statistics.pendingReservations')}
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pendingReservations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reservation:statistics.averageDuration')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.averageDuration.toFixed(1)} {t('reservation:common.days')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Graphiques */}
          <Grid container spacing={3}>
            {/* Réservations par jour */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('reservation:statistics.dailyReservations')}
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stats.dailyReservations}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="reservations" name={t('reservation:statistics.reservations')} fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Utilisation par véhicule */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('reservation:statistics.vehicleUsage')}
                </Typography>
                <Box sx={{ height: 300 }}>
                  {stats.vehicleUsage.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.vehicleUsage}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }: { name: string, percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {stats.vehicleUsage.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('reservation:common.noData')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Utilisation par flotte */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('reservation:statistics.fleetUsage')}
                </Typography>
                <Box sx={{ height: 300 }}>
                  {stats.fleetUsage.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.fleetUsage}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }: { name: string, percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {stats.fleetUsage.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('reservation:common.noData')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ReservationStats;
